import { groupsApi } from '@/modules/iam/api/groups-api'
import { skipToken, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'

import { ApiError, ignoreAlreadyGone } from '@/lib/api'
import { apiErrorMessage } from '@/lib/api-error-message'
import { type CreateEntityGroupInput } from '@/modules/iam/api/groups-api'
import { formatPermissionError } from '@/lib/permissions'
import { groupMembersGridContract, groupsGridContract, useDataGrid } from '@/modules/data-grid'
import { groupKeys, iamInvalidates, assistantEffectMeta } from '../../api/query-keys'

export function createGroupErrorMessage(error: unknown) {
  const fallback = 'Couldn’t create the group. Try again.'
  if (error instanceof ApiError) {
    if (error.code === 'GROUP_NAME_TAKEN')
      return 'A group with this name already exists at this entity.'
    if (error.code === 'ENTITY_NOT_USER_MANAGEMENT_ENTITY')
      return 'Groups can only be created at a user management entity.'
    if (error.status === 403) return formatPermissionError(error.problem)
    return fallback
  }
  return apiErrorMessage(error, fallback)
}

export function grantGroupRoleErrorMessage(error: unknown) {
  const fallback = 'Couldn’t grant that role. Try again.'
  if (error instanceof ApiError) {
    if (error.status === 422)
      return 'That role can’t be granted at this entity.'
    if (error.status !== 403) return fallback
  }
  return apiErrorMessage(error, fallback)
}

export function assignGroupRoleErrorMessage(error: unknown) {
  const fallback = 'Couldn’t assign the role. Try again.'
  if (
    error instanceof ApiError &&
    error.status !== 403 &&
    error.code !== 'ROLE_NOT_ASSIGNABLE'
  )
    return fallback
  return apiErrorMessage(error, fallback, {
    codes: {
      ROLE_NOT_ASSIGNABLE: 'That role can’t be assigned at this entity.',
    },
  })
}

export function useEntityGroups(entityId: string | null) {
  return useQuery({
    queryKey: groupKeys.list(entityId ?? ''),
    queryFn: entityId === null ? skipToken : () => groupsApi.getEntityGroups(entityId),
    meta: assistantEffectMeta,
  })
}

export function useGroupsGrid(entityId: string | null) {
  return useDataGrid({
    contract: groupsGridContract,
    queryKey: [...groupKeys.list(entityId ?? ''), 'grid'],
    endpoint: `/api/v1/entities/${entityId ?? ''}/groups`,
    enabled: entityId !== null,
    meta: assistantEffectMeta,
  })
}

export function useGroupDetail(entityId: string | null, groupId: string | null) {
  return useQuery({
    queryKey: groupKeys.detail(entityId ?? '', groupId ?? ''),
    queryFn:
      entityId === null || groupId === null
        ? skipToken
        : () => groupsApi.getGroupDetail(entityId, groupId),
    meta: assistantEffectMeta,
  })
}

export function useGroupMembersGrid(entityId: string | null, groupId: string | null) {
  return useDataGrid({
    contract: groupMembersGridContract,
    queryKey: [...groupKeys.members(entityId ?? '', groupId ?? ''), 'grid'],
    endpoint: `/api/v1/entities/${entityId ?? ''}/groups/${groupId ?? ''}/members`,
    enabled: entityId !== null && groupId !== null,
    meta: assistantEffectMeta,
  })
}

export function useGroupMemberCandidates(
  entityId: string | null,
  groupId: string | null,
  search = '',
) {
  const query = search.trim()
  return useInfiniteQuery({
    queryKey: groupKeys.memberCandidates(entityId ?? '', groupId ?? '', query),
    queryFn:
      entityId === null || groupId === null
        ? skipToken
        : ({ pageParam }) =>
            groupsApi.getGroupMemberCandidates(entityId, groupId, { after: pageParam || undefined, query }),
    initialPageParam: '',
    getNextPageParam: (lastPage) =>
      lastPage.pageInfo.hasNextPage ? (lastPage.pageInfo.endCursor ?? undefined) : undefined,
    meta: assistantEffectMeta,
  })
}

export function useAddGroupMember(entityId: string, groupId: string) {
  return useMutation({
    mutationFn: (userId: string) =>
      groupsApi.addGroupMember(entityId, groupId, userId),
    meta: { invalidates: iamInvalidates() },
  })
}

export function useAssignGroupRole(entityId: string) {
  return useMutation({
    mutationFn: ({ groupId, roleId }: { groupId: string; roleId: string }) =>
      groupsApi.assignGroupRole(entityId, groupId, roleId),
    meta: { invalidates: iamInvalidates() },
  })
}

export function useRevokeGroupRole(entityId: string, groupId: string) {
  return useMutation({
    mutationFn: (roleId: string) =>
      ignoreAlreadyGone(() => groupsApi.revokeGroupRole(entityId, groupId, roleId)),
    meta: { invalidates: iamInvalidates() },
  })
}

export function useAssignGroupRoleAt(groupId: string) {
  return useMutation({
    mutationFn: ({ entityId, roleId }: { entityId: string; roleId: string }) =>
      groupsApi.assignGroupRole(entityId, groupId, roleId),
    meta: { invalidates: iamInvalidates() },
  })
}

export function useRevokeGroupRoleAt(groupId: string) {
  return useMutation({
    mutationFn: async ({
      entityId,
      roleId,
    }: {
      entityId: string
      roleId: string
    }) => {
      try {
        await groupsApi.revokeGroupRole(entityId, groupId, roleId)
      } catch (error) {
        if (
          !(
            error instanceof ApiError &&
            error.status === 404 &&
            error.code === 'GROUP_ROLE_ASSIGNMENT_NOT_FOUND'
          )
        )
          throw error
      }
    },
    meta: { invalidates: iamInvalidates() },
  })
}

export function useRemoveGroupMember(entityId: string, groupId: string) {
  return useMutation({
    mutationFn: (userId: string) =>
      groupsApi.removeGroupMember(entityId, groupId, userId),
    meta: { invalidates: iamInvalidates() },
  })
}

export function useCreateGroup(entityId: string) {
  return useMutation({
    mutationFn: (input: CreateEntityGroupInput) =>
      groupsApi.createEntityGroup(entityId, input),
    meta: { invalidates: [groupKeys.root] },
  })
}
